'use client';

//import { useSelector } from '@xstate/store/react';
import { useState, useRef, useEffect } from 'react';

import { FeatureProductHeaderActions } from '@/fo/feature/product/header/actions';
import { FeatureProductInfoMain } from '@/fo/feature/product/info/main';
import { FeatureProductTileMedium } from '@/fo/feature/product/tile/medium';
import ArrowIcon from '@/fo/icons/base/icons/arrow.svg';
import { UiKitBreadcrumbsBase } from '@/fo/ui/kit/breadcrumbs/base';
import { UiProductInfoCarePrograms } from '@/fo/ui/product/info/care-programs';
import { UiProductInfoGroupedComposition } from '@/fo/ui/product/info/grouped-composition';
import { UiProductInfoPanel } from '@/fo/ui/product/info/panel';
import { UiProductInfoTop } from '@/fo/ui/product/info/top';
import { UiProductLayoutPage } from '@/fo/ui/product/layout/page';
import { UiProductPictureMain } from '@/fo/ui/product/picture/main';
import { Product } from '@/fo/ui/product/tile/medium';
import { utilEventConfigurableProduct } from '@/fo/util/event-configurable-product';
import { BreadcrumbsItem, MappedProduct } from '@/fo/util/map-product-data';

import { styles } from './ProductPageClient.styles';

export interface ProductPageClientProps {
  product: MappedProduct;
  breadcrumbs?: BreadcrumbsItem[];
  productId: string;
  isAuthorized: boolean;
  slots: {
    advantages: React.ReactNode;
    delivery: React.ReactNode;
    banners: React.ReactNode;
  };
  programsBrief?: Product[];
  maxStockQuantity: number;
}

export function ProductPageClient({
  product: initialProduct,
  breadcrumbs,
  productId,
  isAuthorized,
  slots,
  programsBrief,
  maxStockQuantity
}: ProductPageClientProps) {
  const stylesSlots = styles();

  const [activeIndex, setActiveIndex] = useState(0);
  const [product, setProduct] = useState(initialProduct);
  const [showScrollTopButton, setShowScrollTopButton] = useState(false);

  const productTop = useRef<HTMLDivElement>(null);
  const productInfo = useRef<HTMLDivElement>(null);

  const scrollToProductTop = () => {
    if (!productTop.current) return;

    productTop.current.scrollIntoView({ behavior: 'smooth' });
  };

  const handleScroll = () => {
    if (!productInfo.current) return;

    const minScrollHeight = productInfo.current.getBoundingClientRect().top;

    if (window.pageYOffset > minScrollHeight) {
      setShowScrollTopButton(true);
    } else {
      setShowScrollTopButton(false);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  });

  useEffect(() => {
    return utilEventConfigurableProduct.onProductSelect((newProduct) => {
      setProduct(newProduct);
    });
  }, []);

  return (
    <>
      <div className={stylesSlots.base()}>
        <UiProductLayoutPage
          slots={{
            top: (
              <div className="flex w-full items-center justify-between md:mt-21">
                {breadcrumbs && (
                  <UiKitBreadcrumbsBase
                    className="max-md:pt-[10px] max-md:pb-[2px] lg:hidden"
                    items={breadcrumbs}
                    small="lg"
                    type="dark"
                  />
                )}
                <UiProductInfoTop
                  className="lg:w-full"
                  url={`product/${productId}`}
                  rating={product.rating}
                />
              </div>
            ),
            picture: (
              <div
                className="scroll-mt-[var(--head-fixed-height)]"
                ref={productTop}
              >
                <UiProductPictureMain selectedProduct={product} />
              </div>
            ),
            main: (
              <FeatureProductInfoMain
                baseProduct={product}
                selectedProduct={product}
                isAuthorized={isAuthorized}
                goToDescription={() => setActiveIndex(0)}
                listName="product-detail-page"
                maxStockQuantity={maxStockQuantity}
              />
            ),
            recommends: (
              <div>
                {programsBrief && Boolean(programsBrief?.length) && (
                  <UiProductInfoCarePrograms products={programsBrief} />
                )}
                {product.items && (
                  <UiProductInfoGroupedComposition products={product.items}>
                    {product.items.map((item, index) => (
                      <FeatureProductTileMedium
                        key={`${item.sku}-${index}`}
                        data={item}
                      />
                    ))}
                  </UiProductInfoGroupedComposition>
                )}
              </div>
            ),
            delivery: slots.delivery,
            advantages: slots.advantages,
            banners: slots.banners
          }}
        />
        <div ref={productInfo}>
          <UiProductInfoPanel
            product={product}
            activeIndex={activeIndex}
            onTabChange={setActiveIndex}
          />
        </div>
      </div>
      <FeatureProductHeaderActions
        product={product}
        productInfoRef={productInfo}
        isAuthorized={isAuthorized}
      />

      <div
        className={`${stylesSlots.scrollTop()} ${showScrollTopButton ? stylesSlots.scrollTopShowAnimation() : ''}`}
        onClick={() => scrollToProductTop()}
      >
        <ArrowIcon className={stylesSlots.scrollTopIcon()} />
      </div>
    </>
  );
}
